import React from "react";
import { Box, Stack } from "@mui/material";
import { Control, Controller, useWatch } from "react-hook-form";
import BaseInput from "@reusables/BaseInput";
import { PickingMutationFormTyping } from "@components/Dashboard/pages/Sales/Picking/components/MutationLayout/types";
import BaseTooltip from "@reusables/BaseTooltip";
import { AlertOctagon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@redux/hooks";
import { isNegativeStockAllowedSelector } from "@redux/features/auth/authSlice";

type QuantityCellProps = {
    index: number;
    control: Control<PickingMutationFormTyping>;
}
export default function QuantityCell({control, index}: QuantityCellProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.picking.general.products" });

    const allowNegativeStock = useAppSelector(isNegativeStockAllowedSelector);

    const row = useWatch({
        control,
        name: `lines.${index}.data`
    });

    if (!row)
        return null;

    if (row?.product?.is_service) {
        return (
            <Box width={80}>
                <Controller
                    name={`lines.${index}.data.picked_quantity`}
                    control={control}
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            hideErrors
                            type="number"

                            sx={{
                                ".MuiInput-input": {
                                    padding: "9.5px"
                                }
                            }}
                        />
                    )}
                />
            </Box>
        )
    } else {
        const remainingRequired = Math.max(0, (row.quantity ?? 0) - (row.already_picked_quantity ?? 0));

        return (
            <Stack
                spacing={"16px"}
            >
                {
                    row.picks?.map((pick, pickIndex) => {
                        const picked = Number(pick.picked_quantity ?? 0);
                        const warnings: string[] = [];

                        if (picked > 0 && picked < remainingRequired) {
                            warnings.push("" + t("quantityPartialPickingWarningTooltip", {picked, remaining: remainingRequired}));
                        }

                        // Without the setting, picking above the location stock is a validation error instead
                        if (allowNegativeStock && pick.outgoing_location && picked > 0 && picked > pick.outgoing_location.in_stock) {
                            warnings.push("" + t("quantityNegativeStockWarningTooltip", {resulting: Number((pick.outgoing_location.in_stock - picked).toFixed(2))}));
                        }

                        return (
                            <Stack
                                key={pickIndex}
                                direction={"row"}
                                spacing={1}
                                alignItems="center"
                            >
                                <Box width={80}>
                                    <Controller
                                        name={`lines.${index}.data.picks.${pickIndex}.picked_quantity`}
                                        control={control}
                                        render={({field, fieldState}) => (
                                            <BaseInput
                                                {...field}
                                                error={fieldState.error}

                                                hideErrors
                                                type="number"

                                                sx={{
                                                    ".MuiInput-input": {
                                                        padding: "9.5px"
                                                    }
                                                }}

                                                disabled={row.quantity === row.already_picked_quantity}
                                            />
                                        )}
                                    />
                                </Box>
                                <span>
                                    {pick.outgoing_location && <>of <span className="font-semibold">{pick.outgoing_location.available_here ?? pick.outgoing_location.in_stock}</span></>}
                                </span>
                                <PickWarnings warnings={warnings} />
                            </Stack>
                        );
                    })
                }
            </Stack>
        )
    }
}

function PickWarnings({ warnings }: { warnings: string[] }) {
    if (!warnings.length) return null;

    return (
        <BaseTooltip
            title={
                <div className="space-y-1">
                    {warnings.map((warning) => (
                        <div key={warning}>{warning}</div>
                    ))}
                </div>
            }
            placement="top"
        >
            <span className="relative inline-flex mb-1">
                <AlertOctagon size={14} color="orange" />
                {/* Count badge, shown only when several warnings share the icon */}
                {warnings.length > 1 && (
                    <span className="absolute -top-[7px] -right-[8px] flex items-center justify-center min-w-[12px] h-[12px] px-[2px] rounded-full bg-[orange] text-accent text-[9px] font-semibold leading-none">
                        {warnings.length}
                    </span>
                )}
            </span>
        </BaseTooltip>
    );
}